import BaseModel from '@core/model';

import EventObjectClassEnum from '@constants/enums/event/objectClass';
import EventTypeEnum from '@constants/enums/event/type';
import ProposalStatusEnum from '@constants/enums/proposal/status';

import EventApi from '@packages/events/api';

import Investor from '@/models/Investor';
import Adviser from '@/models/Adviser';
import Proposal from '@/models/Proposal';
import Shortlist from '@/models/Shortlist';

export class Event extends BaseModel {
    static name = 'Event';
    static entity = 'events';
    static Api = EventApi;

    static fields() {
        return {
            ...super.fields(),
            //
            id: this.string(null).nullable(),
            originated_by: this.string(null).nullable(),
            originated_user: this.attr(null).nullable(),
            originator_context: this.string(null).nullable(),
            type: this.enum(EventTypeEnum).nullable(),
            synthetic: this.boolean(true).nullable(),
            created_at: this.string(null).nullable(),

            media_id: this.string(null).nullable(),
            media_ids: this.attr(null).nullable(),

            investor_id: this.string(null).nullable(),
            investor: this.belongsTo(Investor, 'investor_id'),

            adviser_id: this.string(null).nullable(),
            adviser: this.belongsTo(Adviser, 'adviser_id'),

            offer_id: this.string(null).nullable(),
            offer: this.belongsTo(Investor, 'offer_id'),

            shortlist_id: this.string(null).nullable(),
            shortlist: this.belongsTo(Shortlist, 'shortlist_id'),

            proposal_id: this.string(null).nullable(),
            proposal: this.belongsTo(Proposal, 'proposal_id'),
            new_status: this.enum(ProposalStatusEnum).nullable()
        };
    }

    get is_valid() {
        if (!this.type || !this.payload_fields) {
            return false;
        }

        const requiredFields = this.payload_fields.filter(field => !this.base_payload_fields.includes(field));

        return requiredFields.every(field => {
            return this[`${field}`] != null;
        });
    }

    get object_class() {
        switch (this.type) {
            case EventTypeEnum.INVESTOR_ACCOUNT_AUTHORISED:
            case EventTypeEnum.INVESTOR_MEDIA_CREATED:
            case EventTypeEnum.INVESTOR_MEDIA_CREATED_MULTIPLE:
                return EventObjectClassEnum.INVESTOR;
            case EventTypeEnum.PROPOSAL_CREATED:
            case EventTypeEnum.PROPOSAL_COMPLETED:
            case EventTypeEnum.PROPOSAL_STATUS_UPDATED:
                return EventObjectClassEnum.PROPOSAL;
            case EventTypeEnum.SHORTLIST_NEEDS_ADVISER_FEES:
                return EventObjectClassEnum.SHORTLIST;
            default:
                return null;
        }
    }

    get base_payload_fields() {
        return ['originated_by', 'originator_context', 'type', 'synthetic', 'created_at'];
    }

    get payload_fields() {
        switch (this.type) {
            case EventTypeEnum.INVESTOR_ACCOUNT_AUTHORISED:
                return [...this.base_payload_fields, 'investor_id', 'adviser_id'];
            case EventTypeEnum.INVESTOR_MEDIA_CREATED:
                return [...this.base_payload_fields, 'investor_id', 'media_id'];
            case EventTypeEnum.INVESTOR_MEDIA_CREATED_MULTIPLE:
                return [...this.base_payload_fields, 'investor_id', 'media_ids'];
            case EventTypeEnum.PROPOSAL_CREATED:
                return [...this.base_payload_fields, 'proposal_id'];
            case EventTypeEnum.PROPOSAL_COMPLETED:
                return [...this.base_payload_fields, 'proposal_id'];
            case EventTypeEnum.PROPOSAL_STATUS_UPDATED:
                return [...this.base_payload_fields, 'proposal_id', 'new_status'];
            case EventTypeEnum.SHORTLIST_NEEDS_ADVISER_FEES:
                return [...this.base_payload_fields, 'proposal_id'];
            default:
                return null;
        }
    }

    $toPayload() {
        const eventData = super.$toPayload();

        if (this.payload_fields) {
            for (const key in eventData) {
                if (!this.payload_fields.includes(key) && !key.startsWith('$')) {
                    delete eventData[`${key}`];
                }
            }
        }

        return eventData;
    }

    get new_media_id() {
        return null;
    }

    set new_media_id(mediaId) {
        if (!mediaId) {
            return;
        }
        if (this.payload_fields.includes('media_id')) {
            this.media_id = mediaId;
        }
        if (this.payload_fields.includes('media_ids')) {
            this.media_ids = [...new Set([...(this.media_ids || []), mediaId])];
        }
    }
}

export default Event;
